import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { RUNTIME_BACKEND_URL } from '../../../lib/backendUrl';

export const adSrc = (url) => (url && url.startsWith('/api/') ? `${RUNTIME_BACKEND_URL}${url}` : url);

const AdLink = ({ ad, className, children, testId }) => (ad.link
  ? <a href={ad.link} target="_blank" rel="noopener noreferrer sponsored" className={className} data-testid={testId}>{children}</a>
  : <div className={className} data-testid={testId}>{children}</div>);

export const AdSlider = ({ ads }) => {
  const [idx, setIdx] = useState(0);
  const n = ads.length;

  useEffect(() => {
    if (n < 2) return undefined;
    const t = setInterval(() => setIdx(i => (i + 1) % n), 5000);
    return () => clearInterval(t);
  }, [n]);

  if (!n) return null;
  const go = (d) => setIdx(i => (i + d + n) % n);

  return (
    <div className="relative w-full overflow-hidden rounded-2xl border border-white/10 shadow-2xl aspect-[3/1] bg-slate-900" data-testid="ad-slider">
      {ads.map((ad, i) => (
        <AdLink key={ad.id} ad={ad} testId={`ad-slide-${i}`}
          className={`absolute inset-0 transition-opacity duration-700 ${i === idx ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}>
          <img src={adSrc(ad.image_url)} alt={ad.title || 'Reklam'} className="w-full h-full object-cover" />
          {(ad.title || ad.description) && (
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent flex flex-col justify-end p-5 sm:p-8">
              {ad.title && <h3 className="text-white text-lg sm:text-2xl font-bold max-w-xl">{ad.title}</h3>}
              {ad.description && <p className="text-white/80 text-xs sm:text-sm mt-1 max-w-xl">{ad.description}</p>}
            </div>
          )}
        </AdLink>
      ))}
      {n > 1 && (
        <>
          <button type="button" onClick={() => go(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center" data-testid="ad-slider-prev"><ChevronLeft className="h-5 w-5" /></button>
          <button type="button" onClick={() => go(1)} className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center" data-testid="ad-slider-next"><ChevronRight className="h-5 w-5" /></button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex gap-1.5">
            {ads.map((ad, i) => (
              <button key={ad.id} type="button" onClick={() => setIdx(i)} aria-label={`Reklam ${i + 1}`}
                className={`h-1.5 rounded-full transition-[width,background-color] ${i === idx ? 'w-6 bg-white' : 'w-1.5 bg-white/50'}`} data-testid={`ad-slider-dot-${i}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const AdCards = ({ ads, testId }) => {
  if (!ads.length) return null;
  const cols = ads.length === 1 ? '' : ads.length === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3';
  return (
    <div className={`grid gap-4 ${cols}`} data-testid={testId}>
      {ads.map((ad, i) => (
        <AdLink key={ad.id} ad={ad} testId={`${testId}-item-${i}`}
          className="group block overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] hover:border-emerald-400/50 transition-colors">
          <div className={`overflow-hidden ${ads.length === 1 ? 'aspect-[4/1]' : 'aspect-[16/9]'}`}>
            <img src={adSrc(ad.image_url)} alt={ad.title || 'Reklam'} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          </div>
          {(ad.title || ad.description) && (
            <div className="p-4">
              {ad.title && <h4 className="text-white font-semibold">{ad.title}</h4>}
              {ad.description && <p className="text-slate-400 text-sm mt-1">{ad.description}</p>}
            </div>
          )}
        </AdLink>
      ))}
    </div>
  );
};
